import {
    createApp,
    ref,
    type App,
    type Ref,
    type Component,
    type ComponentPublicInstance
} from 'vue';
import { createAppComponent } from './createAppComponent';
import { getComponentIdentifier } from './getComponentIdentifier';
import type { InferComponentProps } from '../../types';

declare global {
    interface Window {
        __singletonComponentInstancesMap?: Map<string, SingletonComponentInstance<Component>>;
    }
}

const getInstancesMap = (): Map<string, SingletonComponentInstance<Component>> => {
    if (typeof window === 'undefined') {
        return new Map();
    }
    
    if (!window.__singletonComponentInstancesMap) {
        window.__singletonComponentInstancesMap = new Map<string, SingletonComponentInstance<Component>>();
    }
    
    // @ts-ignore: код выше гарантирует наличие поля
    return window.__singletonComponentInstancesMap;
};

export class SingletonComponentInstance<T extends Component> {
    //private component: T;
    // private readonly componentId: string; // TODO: typecheck почему-то ругается на приватное поле, потом надо разобраться
    readonly componentId: string;

    public readonly ref = ref() as Ref<ComponentPublicInstance<T>>;
    public readonly app: App;
    public readonly container: HTMLElement;

    constructor(
        component: T,
        componentProps?: InferComponentProps<T>
    ) {
        //this.component = component;
        this.componentId = getComponentIdentifier(component);
        
        // create DOM-element as container
        this.container = document.createElement('div');
        
        // component-name to data attribute
        this.container.setAttribute('data-singleton-component', this.componentId);
        
        document.body.appendChild(this.container);
        
        // create app
        this.app = createApp(
            createAppComponent(
                component,
                this.ref,
                this.destroy.bind(this),
                componentProps
            )
        );
        
        // mount app
        this.app.mount(this.container);
    }

    public destroy(): void {
        this.app.unmount();
        
        if (this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
                
        const instancesMap = getInstancesMap();
        instancesMap.delete(this.componentId);
    }

    public static getInstance<T extends Component>(component: T): SingletonComponentInstance<T> {
        const componentId = getComponentIdentifier(component);
        const instancesMap = getInstancesMap();
                
        let instance = instancesMap.get(componentId);
        
        if (!instance) {            
            instance = new SingletonComponentInstance(component);
            instancesMap.set(componentId, instance);
        }
        
        return instance as SingletonComponentInstance<T>;
    }
}